import Vue from 'vue'
import { checkESign, stopExpireNotice, updateContractOrgState } from '@/api/domain/organize'
import { warnTypeEnum } from '@/config'

// 组件
import AuthDialog from '@/pages/organize/eSign/authDialog'
import CheckDialog from '@/pages/organize/eSign/checkDialog.vue'


let authDialogInstance = null
let checkDialogInstance = null

const getAuthDialogInstance = () => {
  if (!authDialogInstance) {
    const AuthDialogConstructor = Vue.extend(AuthDialog)
    authDialogInstance = new AuthDialogConstructor()
    authDialogInstance.$mount()
    document.body.appendChild(authDialogInstance.$el)
  }
  return authDialogInstance
}

const getCheckDialogInstance = () => {
  if (!checkDialogInstance) {
    const CheckDialogConstructor = Vue.extend(CheckDialog)
    checkDialogInstance = new CheckDialogConstructor()
    checkDialogInstance.$mount()
    document.body.appendChild(checkDialogInstance.$el)
  }
  return checkDialogInstance
}

/**
 * useESignRender Hook
 * 根据不同的传入场景触发不同的渲染结果
 */
export function useESignRender() {
  /**
   * Fetch E-Sign Information
   * @param {Object} params
   * @param {string} params.type - 'dialog' (弹窗类型) | 'text' (文字类型)
   * @param {string} [params.source] - 'workbench' (工作台) | 'goods' (商品) - 仅在 type 为 'text' 时需要
   * @param {Object} [params.params] - 接口请求参数
   * @param {Function} [params.success] - 校验通过后的回调函数
   * @param {Function} [params.fail] - 校验不通过（显示弹窗）后的回调函数
   * @param {Function} [params.noRemind] - 点击不再提醒的回调函数
   */
  const fetchESignInfo = async ({ type, params = {}, success, fail, noRemind }) => {
    try {
      if (type === 'dialog') {
        const { code, data } = await checkESign(params)
        if (code === 1) {
          if (data.warnType !== warnTypeEnum.Normal) {
            const { warnType, expireTime, expireDays, orgName, orgId } = data || {}

            const message = getESignMessage(warnType, expireTime, expireDays)
            const subMessage = getESignSubMessage(warnType)
            const title = getESignTitle(warnType)
            const confirmButtonText = getESignConfirmButtonText(warnType)
            const cancelButtonText = getESignCancelButtonText(warnType)

            // 自定义图标 SVG
            const iconHtml = getESignIcon(warnType)

            const checkDialog = getCheckDialogInstance()
            const { action } = await checkDialog.show({
              title,
              message,
              subMessage,
              iconHtml,
              confirmButtonText,
              cancelButtonText,
              showNoRemind: warnType === warnTypeEnum.ExpireSoon,
            })

            if (action === 'confirm') {
              if (data?.warnType === warnTypeEnum.AuthorizedNotEnable) {
                const { code: updateCode } = await updateContractOrgState({
                  orgId: data.orgId,
                  state: 1,
                })
                if (updateCode === 1) {
                  return success?.()
                }
              }
              const authDialog = getAuthDialogInstance()
              authDialog.showDialog({
                orgName,
                orgId: data.orgId,
                orgIdCardNum: data.orgIdCardNum,
                disabled: true,
                reNew: data.warnType === warnTypeEnum.ExpireSoon ? 1 : 0,
              })
              return fail?.()
            } else if (action === 'cancel') {
              if (data.warnType === warnTypeEnum.ExpireSoon) {
                return success?.()
              }
              fail?.()
            } else if (action === 'noRemind') {
              noRemind?.()
              // 不再提醒通常也意味着此次操作取消或者继续（取决于业务逻辑，此处保持和取消一致的处理逻辑）
              if (data.warnType === warnTypeEnum.ExpireSoon) {
                await stopExpireNotice({ orgId })
                return success?.()
              }
              fail?.()
            }
          } else {
            return success?.()
          }
        }
      }
    } catch (err) {
      console.error('useESignRender error:', err)
      fail?.()
    }
  }

  const getESignIcon = (warnType) => {
    const containerStyle = `
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
    `

    switch (warnType) {
      case warnTypeEnum.Unauthorized:
      case warnTypeEnum.Authorizing:
        return `
          <div style="${containerStyle} background: #FFFBE6;">
            <svg width="22" height="22" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
              <path d="M512 64l448 832H64L512 64z" fill="#FAAD14"/>
              <path d="M512 320c26.5 0 48 21.5 48 48v256c0 26.5-21.5 48-48 48s-48-21.5-48-48V368c0-26.5 21.5-48 48-48zM512 768c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48z" fill="#FFF"/>
            </svg>
          </div>
        `
      case warnTypeEnum.Expire:
        return `
          <div style="${containerStyle} background: #FFF1F0;">
            <i class="el-icon-close" style="font-size: 22px; color: #FF4D4F;"></i>
          </div>
        `
      case warnTypeEnum.ExpireSoon:
        return `
          <div style="${containerStyle} background: #FFF1F0;">
            <i class="el-icon-time" style="font-size: 22px; color: #FF4D4F;"></i>
          </div>
        `
      case warnTypeEnum.AuthorizedNotEnable:
        return `
          <div style="${containerStyle} background: #FFF7E6;">
            <i class="el-icon-lock" style="font-size: 22px; color: #FA8C16;"></i>
          </div>
        `
      default:
        return ''
    }
  }

  const getESignMessage = (warnType, expireTime, expireDays) => {
    switch (warnType) {
      case warnTypeEnum.Unauthorized:
      case warnTypeEnum.Authorizing:
        return '您尚未授权E签宝印章，无法发起线上签约'
      case warnTypeEnum.Expire:
        return `您的E签宝印章授权已于${expireTime}过期`
      case warnTypeEnum.ExpireSoon:
        return `您的E签宝印章授权将于${expireDays}天后过期`
      case warnTypeEnum.AuthorizedNotEnable:
        return '印章被禁用后将无法进行签约操作。如需继续签约，请先启用印章状态。'
      default:
        return '您尚未授权E签宝印章，无法发起线上签约'
    }
  }

  const getESignSubMessage = (warnType) => {
    switch (warnType) {
      case warnTypeEnum.Unauthorized:
      case warnTypeEnum.Authorizing:
        return '为了保障合同签署的法律效力，请先完成E签宝印章授权，授权后即可开始使用线上签约功能。'
      case warnTypeEnum.Expire:
        return '印章授权过期将无法发起新的线上签约，请重新进行授权以继续使用相关功能。'
      case warnTypeEnum.ExpireSoon:
        return '为避免影响线上签约功能的正常使用，建议您提前进行授权续期。'
      case warnTypeEnum.AuthorizedNotEnable:
        return ''
      default:
        return '为了保障合同签署的法律效力，请先完成E签宝印章授权，授权后即可开始使用线上签约功能。'
    }
  }

  const getESignTitle = (warnType) => {
    switch (warnType) {
      case warnTypeEnum.Unauthorized:
      case warnTypeEnum.Authorizing:
        return 'E签宝印章授权'
      case warnTypeEnum.Expire:
        return 'E签宝印章授权已过期'
      case warnTypeEnum.ExpireSoon:
        return 'E签宝印章授权即将过期'
      case warnTypeEnum.AuthorizedNotEnable:
        return 'E签宝印章未启用'
      default:
        return 'E签宝印章授权'
    }
  }

  const getESignConfirmButtonText = (warnType) => {
    switch (warnType) {
      case warnTypeEnum.Unauthorized:
      case warnTypeEnum.Authorizing:
        return '立即授权'
      case warnTypeEnum.Expire:
        return '重新授权'
      case warnTypeEnum.ExpireSoon:
        return '立即续期'
      case warnTypeEnum.AuthorizedNotEnable:
        return '启用并继续签约'
      default:
        return '立即授权'
    }
  }

  const getESignCancelButtonText = (warnType) => {
    if (warnTypeEnum.ExpireSoon === warnType) {
      return '稍后处理'
    }
    return '取消'
  }

  return {
    fetchESignInfo,
  }
}
